<template>
  <div>
    <!-- modal vertical center -->
    <b-modal
      id="admin-modal"
      size="xl"
      :title="`${selectedAction} User: ${user.first_name ? user.first_name : ''} ${user.last_name ? user.last_name : ''}`"
      @show="resetModal"
      @hidden="resetModal"
      ok-only
    >
      <validation-observer ref="userForm">
        <b-form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-6 col-sm-12">
              <!-- first name + last name -->
              <div class="mb-2">
                <label for="first_name">First Name</label>

                <validation-provider
                  #default="{ errors }"
                  name="First Name"
                  rules="required"
                >
                  <b-form-input
                    id="first_name"
                    v-model="user.first_name"
                    name="email"
                    :state="errors.length > 0 ? false : null"
                    :disabled="selectedAction === 'View'"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>

              <div class="mb-2">
                <label for="last_name">Last Name</label>

                <validation-provider
                  #default="{ errors }"
                  name="Last Name"
                  rules="required"
                >
                  <b-form-input
                    id="last_name"
                    v-model="user.last_name"
                    name="last_name"
                    :state="errors.length > 0 ? false : null"
                    :disabled="selectedAction === 'View'"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>

              <div class="mb-2">
                <label for="email">Email</label>

                <validation-provider
                  #default="{ errors }"
                  name="Email"
                  rules="required|email"
                >
                  <b-form-input
                    id="email"
                    v-model="user.email"
                    name="email"
                    :state="errors.length > 0 ? false : null"
                    placeholder="john@example.com"
                    :disabled="selectedAction === 'View'"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>

              <div class="mb-2" :hidden="selectedAction !== 'Edit'">
                <label for="password">Password (6 characters or longer)</label>
                <div class="d-flex align-items-center">
                  <b-form-input
                    id="email"
                    v-model="password"
                    name="password"
                    type="password"
                    minlength="6"
                    :disabled="selectedAction === 'View'"
                  />

                  <b-button class="ml-2" type="button" :disabled="password.length < 6" @click="updatePassword">{{
                    submittingPassword ? "Submitting..." : "Update"
                  }}</b-button>
                </div>
              </div>

              <div class="mb-2">
                <label for="status">Status</label>

                <validation-provider
                  #default="{ errors }"
                  name="Status"
                  rules="required"
                >
                  <b-form-select
                    id="status"
                    v-model="user.status"
                    name="status"
                    :options="['INACTIVE', 'ACTIVE', 'imported']"
                    :state="errors.length > 0 ? false : null"
                    :disabled="selectedAction === 'View'"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>

              <div class="mb-2">
                <label for="account_type">Account Type</label>

                <validation-provider
                  #default="{ errors }"
                  name="Account Type"
                  rules="required"
                >
                  <b-form-select
                    id="account_type"
                    v-model="user.account_type"
                    name="account_type"
                    :options="['year', 'lifetime']"
                    :state="errors.length > 0 ? false : null"
                    :disabled="selectedAction === 'View'"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </div>

              <div class="mb-2">
                <label for="token">Activation Token (ex. 12345)</label>

                <div v-if="selectedAction === 'Create'">
                  <validation-provider
                    #default="{ errors }"
                    name="token"
                    rules="required"
                  >
                    <b-form-input
                      id="token"
                      name="token"
                      :state="errors.length > 0 ? false : null"
                      v-model="user.token"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </div>
                <div v-else>
                  <b-form-input
                    id="token"
                    name="token"
                    :disabled="selectedAction === 'View'"
                    v-model="user.token"
                  />
                </div>
              </div>

              <div class="mb-2">
                <label for="expires_at">Activation Token Expires At</label>

                <div v-if="selectedAction === 'Create'">
                  <validation-provider
                    #default="{ errors }"
                    name="expires_at"
                    rules="required"
                  >
                    <b-form-datepicker
                      id="expires_at"
                      name="expires_at"
                      v-model="user.expires_at"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </div>
                <div v-else>
                    <b-form-datepicker
                      id="expires_at"
                      name="expires_at"
                      :disabled="selectedAction === 'View'"
                      v-model="user.expires_at"
                    />
                </div>
              </div>

              <div class="mb-2">
                <label for="activated_at">Activated At</label>

                <b-form-datepicker
                  id="activated_at"
                  name="activated_at"
                  :disabled="selectedAction === 'View'"
                  v-model="user.activated_at"
                />
              </div>
            </div>
            <div class="col-lg-6 col-sm-12">
              <div class="mb-2">
                <label for="forget_password_token">Forget Password Token</label>

                <b-form-input
                  id="forget_password_token"
                  name="forget_password_token"
                  :disabled="selectedAction === 'View'"
                  v-model="user.forget_password_token"
                />
              </div>

              <div class="mb-2">
                <label for="forget_password_token_expiry"
                  >Forget Password Token Expiry</label
                >

                <b-form-datepicker
                  id="forget_password_token_expiry"
                  name="forget_password_token_expiry"
                  :disabled="selectedAction === 'View'"
                  v-model="user.forget_password_token_expiry"
                />
              </div>
              <div class="mb-2" v-if="selectedAction === 'Edit'">
                <label for="send_activation_email"
                  >Send Activation Token Email</label
                >

                <!-- activation email -->
                <b-button
                  style="max-width: 250px;"
                  variant="warning"
                  id="send_activation_email"
                  block
                  v-if="selectedAction === 'Edit'"
                  @click="sendActivationEmail"
                >
                  Send Email
                </b-button>
              </div>

              <!-- readonly input fields -->
              <div v-if="selectedAction === 'View'">
                <div class="mb-2">
                  <label for="role">Role</label>

                  <b-form-input id="role" :value="user.role" name="role" disabled />
                </div>

                <div class="mb-2">
                  <label for="last_logged_in">Last Logged In Dashboard</label>

                  <b-form-input
                    id="last_logged_in"
                    :value="handleDateFormat(user.last_logged_in)"
                    name="last_logged_in"
                    disabled
                  />
                </div>

                <div class="mb-2">
                  <label for="last_logged_in_app">Last Logged In App</label>

                  <b-form-input
                    id="last_logged_in"
                    :value="handleDateFormat(user.last_logged_in_app)"
                    name="last_logged_in_app"
                    disabled
                  />
                </div>

                <div class="mb-2">
                  <label for="logged_in_count">Total Number Of Logins In The App</label>

                  <b-form-input
                    id="logged_in_count"
                    :value="loginHistory.length"
                    name="logged_in_count"
                    disabled
                  />
                </div>

                <div class="mb-2">
                  <label for="created_at">Created At</label>

                  <b-form-input
                    id="expires_at"
                    :value="handleDateFormat(user.created_at)"
                    name="created_at"
                    disabled
                  />
                </div>

                <div class="mb-2">
                  <label for="deleted_at">Deleted At</label>

                  <b-form-input
                    id="deleted_at"
                    :value="handleDateFormat(user.deleted_at)"
                    name="deleted_at"
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="mb-2" v-if="selectedAction !== 'Create'">
            <label for="email">App Login History</label>

            <div v-if="loginHistoryRead !== ''">
              <b-form-textarea
                id="loginHistoryRead"
                v-model="loginHistoryRead"
                :disabled="true"
                rows="10"
              />
            </div>
            <div v-else>No login history found for <b>"{{user.first_name}}"</b></div>
          </div>

          <div class="mb-2" >
            <label for="email">User Updates</label>

            <div>
              <b-form-textarea
                id="user.notes"
                v-model="user.notes"
                :disabled="selectedAction === 'View'"
                rows="10"
              />
            </div>
          </div>

          <!-- submit button -->
          <b-button
            variant="primary"
            type="submit"
            block
            v-if="selectedAction !== 'View'"
          >
            Save
          </b-button>
        </b-form>
      </validation-observer>

      <template #modal-footer>
        {{ "" }}
      </template>
    </b-modal>

    <b-modal
      id="delete-modal"
      title="Delete User"
      @show="resetModal"
      @hidden="resetModal"
      ok-variant="danger"
      @ok="handleDelete"
    >
      <div>Are you sure you want to remove <b>{{user.first_name}} {{user.last_name}} ({{user.email}})</b>?</div>
    </b-modal>

    <!-- Table Container Card -->
    <b-card no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Entries</label>

            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />

            <b-button
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="primary"
              @click="handleAction('create')"
            >
              Add User
            </b-button>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <!-- Search input -->
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block"
                placeholder="First name, Last name or Email..."
              />
              <!-- Clear serach input button -->
              <b-button
                class="clear-button"
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                variant="primary"
                @click="() => this.searchQuery = ''"
              >
                Clear
              </b-button>
              <v-select
                v-model="statusFilter"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="statusOptions"
                class="invoice-filter-select"
                placeholder="Select Status"
              >
                <template #selected-option="{ label }">
                  <span class="text-truncate overflow-hidden">
                    {{ label }}
                  </span>
                </template>
              </v-select>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        :busy="isBusy"
        :items="users"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        class="position-relative"
      >
        <!-- loading -->
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>Loading...</strong>
          </div>
        </template>

        <!-- Column: Id -->
        <template #cell(id)="data">
          <span class="font-weight-bold"> #{{ data.index + 1 }} </span>
        </template>

        <!-- Column: Last Logged In -->
        <template #cell(last_logged_in)="data">
          {{ handleDateFormat(data.item.last_logged_in) }}
        </template>

        <!-- Column: Last Logged In App-->
        <template #cell(last_logged_in_app)="data">
          {{ handleDateFormat(data.item.last_logged_in_app) }}
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button class="action-btn" size="sm" variant="primary" @click="handleAction('view', data)">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">View</span>
          </b-button>
          <b-button class="action-btn" size="sm" variant="warning" @click="handleAction('edit', data)">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">Edit</span>
          </b-button>
          <b-button class="action-btn" size="sm" variant="danger" @click="handleAction('delete', data)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">Delete</span>
          </b-button>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Showing {{ pagination.currentPage }} to
              {{ pagination.lastPage }} of {{ pagination.total }} entries</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="pagination.currentPage"
              :total-rows="pagination.total"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>

              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from "@/auth/jwt/useJwt";
import vSelect from "vue-select";
import debounce from "lodash.debounce";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import handleDateFormat from "@core/utils/handleDateFormat";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import {
  BModal,
  BButton,
  VBModal,
  BCard,
  BRow,
  BCol,
  BForm,
  BFormInput,
  BFormSelect,
  BTable,
  BPagination,
  BSpinner,
  BFormTextarea,
  BFormDatepicker,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";

export default {
  components: {
    BButton,
    BModal,
    BCard,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BTable,
    BPagination,
    BSpinner,
    BFormDatepicker,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    "b-modal": VBModal,
    Ripple,
  },
  data() {
    return {
      selectedAction: "Create",
      isBusy: false,
      pagination: {
        currentPage: 1,
        lastPage: 1,
        nextPage: 1,
        previousPage: null,
        total: 1,
      },

      perPage: 10,

      users: [],

      tableColumns: [
        { key: "id", label: "S/N", sortable: true },
        { key: "first_name", label: "First Name", sortable: true },
        { key: "last_name", label: "Last Name", sortable: true },
        { key: "role", label: "Role", sortable: true },
        { key: "email", label: "email", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "last_logged_in", label: "Last Logged In Dashboard", sortable: true },
        { key: "last_logged_in_app", label: "Last Logged In App", sortable: true },
        { key: "actions" },
      ],

      perPageOptions: [2, 5, 10, 20, 30, 40, 50],
      searchQuery: "",
      statusOptions: ["inactive", "active"],
      statusFilter: "",
      sortBy: "id",
      isSortDirDesc: false,
      user: {
        id: -1,
        first_name: "",
        last_name: "",
        email: "",
        status: "INACTIVE",
        account_type: "",
        role: "user",
        notes: "",
      },
      loginHistory: [],
      loginHistoryRead: '',
      password: "",
      submittingPassword: false,
      required,
      email,
    };
  },
  watch: {
    searchQuery() {
      this.isBusy = true;
      this.debounceMethod();
    },

    perPage() {
      this.pagination.currentPage = 1;
      this.fetchAccounts();
    },

    statusFilter() {
      this.fetchAccounts();
    },

    "pagination.currentPage"() {
      this.fetchAccounts();
    },
  },
  computed: {
    currentRole() {
      return this.$store.state.account.profile.role;
    },
  },
  methods: {
    handleDateFormat,
    debounceMethod: debounce(function() {
      this.fetchAccounts();
    }, 500),

    resetModal() {
      this.user = {
        first_name: "",
        last_name: "",
        email: "",
        status: "INACTIVE",
        account_type: "",
        role: "user",
      };
    },

    handleAction(actionType, data) {
      if (actionType === "create") {
        this.$root.$emit("bv::show::modal", "admin-modal");
        this.selectedAction = "Create";
        return;
      }

      if (actionType === "view") {
        this.$root.$emit("bv::show::modal", "admin-modal");
        this.selectedAction = "View";
        this.user = { ...data.item };
        this.fetchLoginHistory();
        return;
      }

      if (actionType === "edit") {
        this.$root.$emit("bv::show::modal", "admin-modal");
        this.selectedAction = "Edit";
        this.user = { ...data.item };
        this.fetchLoginHistory();
        return;
      }

      if (actionType === "delete") {
        this.$root.$emit("bv::show::modal", "delete-modal");
        this.user = { ...data.item };
        return;
      }

      throw new Error("Wrong action type");
    },

    async handleSubmit() {
      const result = await this.$refs.userForm.validate();

      if (result) {
        if (this.selectedAction === "Create") {
          await this.handleCreate();
        } else if (this.selectedAction === "Edit") {
          await this.handleEdit();
        } else {
          throw new Error("Wrong selected action type");
        }

        // Hide the modal manually
        this.$nextTick(() => {
          this.$bvModal.hide("admin-modal");
        });
      }
    },

    async handleCreate() {
      const originalUsers = [...this.users];

      try {
        const uniqueIdentifier = Date.now();
        this.users.unshift({ ...this.user, id: uniqueIdentifier });
        const { data } = await useJwt.createAccount({ ...this.user });

        const userIndex = this.users.findIndex(
          (item) => item.id === uniqueIdentifier
        );

        this.users[userIndex].id = data.message.id;

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Success`,
            icon: "CheckmarkIcon",
            variant: "success",
            text: `Account created successfully`,
          },
        });
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Error`,
            icon: "CheckmarkIcon",
            variant: "danger",
            text: `Error creating account, rolling back changes.`,
          },
        });

        // handle in case of an error
        this.users = originalUsers;
      }
    },

    async handleEdit() {
      const originalUsers = [...this.users];

      try {
        const userIndex = this.users.findIndex(
          (item) => item.id === this.user.id
        );

        const clonedUsers = [...this.users];

        clonedUsers[userIndex] = { ...this.user };
        this.users = clonedUsers;

        await useJwt.updateAccount(this.user.id, { ...this.user });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Success`,
            icon: "CheckmarkIcon",
            variant: "success",
            text: `Account updated successfully`,
          },
        });
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Error`,
            icon: "CheckmarkIcon",
            variant: "danger",
            text: `Error updating account, rolling back changes.`,
          },
        });

        // handle in case of an error
        this.users = originalUsers;
      }
    },

    async handleDelete() {
      const originalUsers = [...this.users];
      try {
        // optimistic update happens here
        this.users = this.users.filter((user) => user.id !== this.user.id);

        await useJwt.deleteAccount(this.user.id);

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Success`,
            icon: "CheckmarkIcon",
            variant: "success",
            text: `Account deleted successfully`,
          },
        });
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Error`,
            icon: "CheckmarkIcon",
            variant: "danger",
            text: `Error deleting account, rolling back changes.`,
          },
        });

        // handle in case of an error
        this.users = originalUsers;
      }
    },

    async fetchLoginHistory() {
      this.loginHistory = [];
      this.loginHistoryRead = '';
      const { data } = await useJwt.getLoginHistory({ id: this.user.id});
      this.loginHistory = data.message;
      this.loginHistoryRead = this.loginHistory.length === 0 ? '' : this.loginHistory.reduce((result, item) => result += `Country/region: ${item.country} (${item.region}), Date: ${item.logged_in_at}, ip: ${item.ip} \n`, '');
    },

    async fetchAccounts() {
      try {
        this.isBusy = true;

        const { data } = await useJwt.getAccounts({
          role: "user",
          perPage: this.perPage,
          searchQuery: this.searchQuery.trim(),
          status: this.statusFilter || "",
          page: this.pagination.currentPage,
        });

        this.users = data.message.accounts.map((item) => ({
          ...item,
          role: "user",
        }));

        this.pagination = data.message.meta;
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Error`,
            icon: "CheckmarkIcon",
            variant: "danger",
            text: `Error fetching accounts`,
          },
        });
      } finally {
        this.isBusy = false;
      }
    },

    async updatePassword() {
      try {
        this.submittingPassword = true;
        await useJwt.updatePassword({ id: this.user.id, password: this.password, owner: false });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Success`,
            icon: "CheckmarkIcon",
            variant: "success",
            text: `Password updated successfully`,
          },
        });
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Error`,
            icon: "CheckmarkIcon",
            variant: "danger",
            text: `Error updating password`,
          },
        });
      } finally {
        this.submittingPassword = false;
        // Hide the modal manually
        this.$nextTick(() => {
          this.$bvModal.hide("admin-modal");
        });
      }
    },

    async sendActivationEmail() {
      try {
        await useJwt.sendActivationEmail({ id: this.user.id });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Success`,
            icon: "CheckmarkIcon",
            variant: "success",
            text: `Activation email sent`,
          },
        });
      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: `Error`,
            icon: "CheckmarkIcon",
            variant: "danger",
            text: `Error sending activation email`,
          },
        });
      } finally {
        this.submittingPassword = false;
        // Hide the modal manually
        this.$nextTick(() => {
          this.$bvModal.hide("admin-modal");
        });
      }
    },
  },
  created() {
    this.fetchAccounts();
  },
};
</script>

<style lang="scss" scoped>
.clear-button {
  margin-right: 20px;
}

.per-page-selector {
  width: 90px;
}

.action-btn {
  margin-left: 10px;
  margin-right: 10px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
